import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, ArrowUpRight } from "lucide-react";
import { api, API, errMsg } from "@/lib/api";
import { RelocImport } from "@/components/reloc/RelocImport";
import { RelocShop } from "@/components/reloc/RelocShop";
import { NewRelocGuide } from "@/components/reloc/NewRelocGuide";
import { UploadKit } from "@/components/reloc/UploadKit";
import { UploadGuide } from "@/components/reloc/UploadGuide";
import { RelocLinks } from "@/components/reloc/RelocLinks";
import { RelocRates } from "@/components/reloc/RelocRates";

const statusOf = (g) => (g.running ? ["text-[#C59B27]", `Building ${g.progress || 0}%`] : g.status === "failed" ? ["text-red-700", "Build stopped"] : g.published ? ["text-[#2D6A4F]", "On the store"] : ["text-[#8C827A]", "Draft"]);

const GuideRow = ({ g }) => {
  const [cls, label] = statusOf(g);
  return (
    <Link to={`/relocation/${g.id}`} data-testid={`reloc-card-${g.id}`} className="grid grid-cols-[48px_1fr_auto] sm:grid-cols-[48px_1.6fr_110px_110px_130px_90px_20px] items-center gap-4 px-5 py-3 border-b border-[#E2D9CC] last:border-0 hover:bg-[#EFE6F3]/60 transition-colors group">
      <div className="w-12 h-16 bg-[#3B1B48] overflow-hidden">{g.has_cover && <img src={`${API}/relocation/guides/${g.id}/files/cover?inline=true`} alt="" loading="lazy" className="w-full h-full object-cover" />}</div>
      <div className="min-w-0">
        <p className="font-display text-lg text-[#3B1B48] leading-tight truncate">{g.title}</p>
        <p className="text-xs text-[#8C827A] mt-0.5">{g.origin} → {g.destination}{g.family && <span data-testid={`reloc-family-${g.id}`} className="ml-2 px-1.5 py-0.5 bg-[#EFE6F3] text-[#3B1B48]">Families</span>}</p>
      </div>
      <span className="hidden sm:block text-sm text-[#1C181D]">{g.chapters} chapters</span>
      <span className="hidden sm:block text-sm text-[#1C181D]">Edition {g.edition}</span>
      <span data-testid={`reloc-status-${g.id}`} className={`text-xs font-mono-accent uppercase tracking-wider ${cls}`}>{label}</span>
      <span className="hidden sm:block text-sm text-[#3B1B48]">from A${g.price_from}</span>
      <ArrowUpRight className="hidden sm:block w-4 h-4 text-[#8C827A] group-hover:text-[#3B1B48] transition-colors" />
    </Link>
  );
};

const Group = ({ id, title, blurb, guides }) => {
  const [open, setOpen] = useState(true);
  const live = guides.filter((g) => g.published).length;
  return (
    <section data-testid={`reloc-group-${id}`} className="border border-[#E2D9CC] bg-[#FAF7F2]">
      <button data-testid={`reloc-group-toggle-${id}`} onClick={() => setOpen(!open)} aria-expanded={open} className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-[#F4EFE6] transition-colors">
        <ChevronDown className={`w-5 h-5 text-[#3B1B48] transition-transform duration-200 ${open ? "" : "-rotate-90"}`} strokeWidth={1.5} />
        <span className="font-display text-2xl text-[#3B1B48]">{title}</span>
        <span className="text-sm text-[#8C827A] hidden sm:inline">{blurb}</span>
        <span className="ml-auto text-xs font-mono-accent uppercase tracking-wider text-[#8C827A]" data-testid={`reloc-group-count-${id}`}>{guides.length} guides · {live} on the store</span>
      </button>
      {open && (
        <div className="border-t border-[#E2D9CC] bg-white" data-testid={`reloc-group-list-${id}`}>
          {guides.map((g) => <GuideRow key={g.id} g={g} />)}
          {!guides.length && <p className="px-5 py-6 text-sm text-[#8C827A]">No guides here yet.</p>}
        </div>
      )}
    </section>
  );
};

export default function RelocationPage() {
  const [guides, setGuides] = useState(null);
  const load = useCallback(() => api.get("/relocation/guides").then((r) => setGuides(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const live = guides?.filter((g) => g.published).length || 0;

  return (
    <div className="rise space-y-12">
      <div className="flex flex-wrap items-end justify-between gap-6">
       <div>
        <p className="eyebrow mb-3">The Expat Studio · Relocation Guides</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#3B1B48] leading-none">Relocation Guides</h1>
        <p className="mt-4 text-[#8C827A] max-w-2xl">Three-volume country guides for Australians moving abroad, each with a complete edition, a free sample, a toolkit and a budget planner.</p>
        <p className="mt-3 text-sm text-[#3B1B48]" data-testid="reloc-counts">{guides ? `${guides.length} guides · ${live} on the store` : ""}</p>
       </div>
       <div className="flex flex-wrap gap-3"><UploadKit /><UploadGuide /><NewRelocGuide /></div>
      </div>
      <RelocImport onDone={load} />
      {guides?.length === 0 && <p data-testid="reloc-empty" className="text-[#8C827A]">No relocation guides yet. Press Import guides to copy them across.</p>}
      {guides?.length > 0 && (
        <div className="space-y-6" data-testid="reloc-grid">
          <Group id="standard" title="Standard" blurb="For individuals and couples" guides={guides.filter((g) => !g.family)} />
          <Group id="families" title="Families" blurb="Moving with children" guides={guides.filter((g) => g.family)} />
        </div>
      )}
      <RelocShop />
      <RelocLinks />
      <RelocRates />
    </div>
  );
}
